import { Button } from '@spotnana/blocks/src/Button';
import { useModalState } from '@spotnana/blocks/src/Modal/useModalState';
import {
  AirSelectedItineraryResponseManager,
  convertSegmentsToAirSuggestionArrays,
  createUserNameFromFullName,
  getFullName,
  IFlightCheckoutFormParams,
  ITraveler,
  useFeatureFlag,
  Fare,
  RequiredPaxUrlInfo,
} from 'obt-common';
import { FC, useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'rhf-v7';
import { AddToShareItineraryListModal } from 'src/app/flights/SavedFaresPage/AddToShareItineraryListModal';
import { useTranslation } from 'react-i18next';
import { useGetAirSearchResultsFeatures } from 'obt-common/hooks/Flights/useGetAirSearchResultsFeatures';
import { additional_cta_button } from './styles';
import { useShareableFareListsForLoggedInUser } from '../../SavedFaresPage/hooks/useShareableListsForLoggedInUser';
import FareAddedConfirmationModal from '../../SavedFaresPage/FareAddedConfirmationModal';
import { ShareItineraryListDrawer } from '../../SavedFaresPage/ShareItineraryListDrawer';
import { getSegmentsWithSelectedAirSuggestion } from '../../SavedFaresPage/utils/getSegmentsWithSelectedAirSuggestion';

export interface IProps {
  airSelectedItineraryResponseManager: AirSelectedItineraryResponseManager;
  itineraryOwner: ITraveler;
}

export const AddToShareItineraryListModalContainer: FC<IProps> = ({
  airSelectedItineraryResponseManager,
  itineraryOwner,
}) => {
  const { t: tt } = useTranslation('WEB');
  const isPreferredNameEnabled = useFeatureFlag('FE_INFRA_AUTOCOMPLETE_PREFERRED_NAME');
  const { watch, getValues } = useFormContext<IFlightCheckoutFormParams>();
  const [selectedListUuid, setSelectedListUuid] = useState<string>();

  const lastLegRateOptionId = watch('lastLegRateOptionId');
  const searchId = watch('searchId');
  const segments = convertSegmentsToAirSuggestionArrays(watch('segments'));
  const registrarId = watch('registrarId');

  const {
    open: isAddToSavedListModalOpen,
    openModal: openAddToSavedListModal,
    closeModal: closeAddToItineraryModal,
  } = useModalState();

  const {
    open: isFareAddedConfirmationModalOpen,
    openModal: openFareAddedConfirmationModal,
    closeModal: closeFareAddedConfirmationModal,
  } = useModalState();

  const {
    open: isShareItineraryListDrawerOpen,
    openModal: openShareItineraryListDrawer,
    closeModal: closeShareItineraryListDrawer,
  } = useModalState();

  const firstPassengerName = itineraryOwner.user?.name;
  const primaryTravelerName = isPreferredNameEnabled
    ? createUserNameFromFullName(firstPassengerName)
    : getFullName(firstPassengerName);

  const { airSearchResultsFeatures } = useGetAirSearchResultsFeatures(registrarId);

  const {
    addFareToFareList,
    createFareListForTraveler,
    loggedInUserFareList,
    isFetching,
    removeFareFromFareList,
    deleteFareList,
    deleteAllSavedItineraries,
  } = useShareableFareListsForLoggedInUser();

  const itineraryOwnerFareLists = useMemo(
    () => loggedInUserFareList.filter((list) => list.travelerUserId === registrarId),
    [loggedInUserFareList, registrarId],
  );

  const onCreateNewList = useCallback(() => {
    if (!registrarId) {
      return;
    }
    const passengers = getValues('passengers');
    const urlPassengers: RequiredPaxUrlInfo[] = passengers.map((passenger) => ({
      paxType: passenger.paxType,
      readOnly: passenger.readOnly,
      paxAge: passenger.paxAge,
      userId: passenger.userId,
    }));

    const singleAirSuggestionSegments = getSegmentsWithSelectedAirSuggestion({
      segments,
      airSelectedItineraryResponseManager,
    });
    const tripBookingFlow = getValues('tripIdInfo.tripBookingFlow');
    const fareToAdd = {
      searchId,
      rateOptionId: lastLegRateOptionId,
      segments: singleAirSuggestionSegments,
      passengers: urlPassengers,
      tripBookingFlow,
    };
    const newFareListUuid = createFareListForTraveler(registrarId, fareToAdd);
    setSelectedListUuid(newFareListUuid);
    openFareAddedConfirmationModal();
    closeAddToItineraryModal();
  }, [
    registrarId,
    getValues,
    segments,
    airSelectedItineraryResponseManager,
    searchId,
    lastLegRateOptionId,
    createFareListForTraveler,
    openFareAddedConfirmationModal,
    closeAddToItineraryModal,
  ]);

  const onAddToExistingList = useCallback(
    (listId: string) => {
      setSelectedListUuid(listId);

      const passengers = getValues('passengers');
      const urlPassengers: RequiredPaxUrlInfo[] = passengers.map((passenger) => ({
        paxType: passenger.paxType,
        readOnly: passenger.readOnly,
        paxAge: passenger.paxAge,
        userId: passenger.userId,
      }));

      const tripBookingFlow = getValues('tripIdInfo.tripBookingFlow');
      const singleAirSuggestionSegments = getSegmentsWithSelectedAirSuggestion({
        segments,
        airSelectedItineraryResponseManager,
      });

      const fareToAdd: Fare = {
        searchId,
        rateOptionId: lastLegRateOptionId,
        segments: singleAirSuggestionSegments,
        passengers: urlPassengers,
        tripBookingFlow,
      };

      addFareToFareList(listId, fareToAdd);
      openFareAddedConfirmationModal();
      closeAddToItineraryModal();
    },
    [
      getValues,
      segments,
      airSelectedItineraryResponseManager,
      searchId,
      lastLegRateOptionId,
      addFareToFareList,
      openFareAddedConfirmationModal,
      closeAddToItineraryModal,
    ],
  );

  const onViewItineraryList = () => {
    openShareItineraryListDrawer();
    closeFareAddedConfirmationModal();
  };

  const onCloseShareItineraryListDrawer = () => {
    closeShareItineraryListDrawer();
    setSelectedListUuid(undefined);
  };

  if (isFetching) {
    return null;
  }

  const selectedList = itineraryOwnerFareLists.find((list) => list.uuid === selectedListUuid);

  return (
    <>
      <Button
        variant="secondary"
        css={additional_cta_button}
        onClick={openAddToSavedListModal}
        startIcon="AddPlusCircle"
      >
        {tt('Add to itinerary list')}
      </Button>
      <AddToShareItineraryListModal
        fareList={itineraryOwnerFareLists}
        onAddToExistingList={onAddToExistingList}
        open={isAddToSavedListModalOpen}
        onClose={closeAddToItineraryModal}
        onCreateNewList={onCreateNewList}
      />
      <FareAddedConfirmationModal
        open={isFareAddedConfirmationModalOpen}
        onClose={closeFareAddedConfirmationModal}
        airSelectedItineraryResponseManager={airSelectedItineraryResponseManager}
        onViewItineraryList={onViewItineraryList}
        travelerName={primaryTravelerName}
        airSearchResultsFeatures={airSearchResultsFeatures}
        selectedList={selectedList}
      />
      {selectedListUuid && (
        <ShareItineraryListDrawer
          currentLists={loggedInUserFareList}
          selectedListUuid={selectedListUuid}
          onClose={onCloseShareItineraryListDrawer}
          open={isShareItineraryListDrawerOpen}
          removeFareFromFareList={removeFareFromFareList}
          deleteFareList={deleteFareList}
          deleteAllSavedItineraries={deleteAllSavedItineraries}
        />
      )}
    </>
  );
};
